import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { useAuth } from '../../shared/auth';
import { Modal } from '../../ui/Modal';

interface User {
  id: string;
  email: string;
  fullName: string;
  role: 'ADMIN' | 'PROVIDER' | 'STAFF';
  active: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

/**
 * Admin-only user management. Provides:
 *   - List of all users (active + locked)
 *   - Create new user (email, fullName, role, password)
 *   - Edit user (fullName, role)
 *   - Lock / unlock account
 *   - Reset password (admin sets new value; out-of-band delivery to user)
 *
 * All mutations are admin-only at the API level; the page also gates UI
 * elements so non-admins land on a "not allowed" message instead of
 * seeing buttons that would 403.
 */
export function UsersPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.role === 'ADMIN';
  const [createOpen, setCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [resettingUser, setResettingUser] = useState<User | null>(null);

  const { data: users, isLoading } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: () => api.get('/users').then((r) => r.data),
    enabled: isAdmin,
  });

  const lock = useMutation({
    mutationFn: (id: string) => api.post(`/users/${id}/lock`).then((r) => r.data),
    onSuccess: () => {
      toast.success('User locked');
      qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Lock failed'),
  });
  const unlock = useMutation({
    mutationFn: (id: string) => api.post(`/users/${id}/unlock`).then((r) => r.data),
    onSuccess: () => {
      toast.success('User unlocked');
      qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Unlock failed'),
  });

  if (!isAdmin) {
    return (
      <div className="card p-6 text-sm text-ink-subtle">
        User management is admin-only.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">Users</h1>
          <p className="text-sm text-ink-subtle">
            Manage user accounts, roles, and access. Locked accounts cannot log in.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setCreateOpen(true)}>
          + New user
        </button>
      </header>

      {isLoading ? (
        <div className="card p-3 text-sm text-ink-subtle">Loading…</div>
      ) : !users || users.length === 0 ? (
        <div className="card p-6 text-sm text-ink-subtle">No users found.</div>
      ) : (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-surface-subtle text-2xs uppercase tracking-wide text-ink-subtle">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Role</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Last login</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-line-subtle hover:bg-surface-subtle/40">
                  <td className="px-3 py-2 font-medium">{u.fullName}</td>
                  <td className="px-3 py-2 text-xs font-mono">{u.email}</td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        u.role === 'ADMIN'
                          ? 'pill-info'
                          : u.role === 'STAFF'
                            ? 'pill-muted'
                            : 'pill-info'
                      }
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {u.active ? (
                      <span className="pill-success">Active</span>
                    ) : (
                      <span className="pill-danger">Locked</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-2xs text-ink-subtle">
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : 'never'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        className="btn-ghost h-7 px-2 text-xs"
                        onClick={() => setEditingUser(u)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn-ghost h-7 px-2 text-xs"
                        onClick={() => setResettingUser(u)}
                      >
                        Reset password
                      </button>
                      {u.active ? (
                        <button
                          className="btn-ghost h-7 px-2 text-xs text-rose-700"
                          onClick={() => {
                            if (u.id === user?.id) {
                              if (!confirm('Lock YOUR OWN account? You will be logged out and unable to log back in until another admin unlocks you.')) {
                                return;
                              }
                            } else if (!confirm(`Lock ${u.email}?`)) {
                              return;
                            }
                            lock.mutate(u.id);
                          }}
                        >
                          Lock
                        </button>
                      ) : (
                        <button
                          className="btn-ghost h-7 px-2 text-xs text-emerald-700"
                          onClick={() => unlock.mutate(u.id)}
                        >
                          Unlock
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && (
        <CreateUserModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            qc.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      )}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => {
            setEditingUser(null);
            qc.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      )}
      {resettingUser && (
        <ResetPasswordModal
          user={resettingUser}
          onClose={() => setResettingUser(null)}
          onReset={() => {
            setResettingUser(null);
            qc.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      )}
    </div>
  );
}

function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<'ADMIN' | 'PROVIDER'>('PROVIDER');
  const [password, setPassword] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api.post('/users', { email, fullName, role, password }).then((r) => r.data),
    onSuccess: () => {
      toast.success('User created');
      onCreated();
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Create failed'),
  });
  return (
    <Modal open onClose={onClose} title="New user" size="md">
      <div className="space-y-3">
        <div>
          <label className="label">Email</label>
          <input
            type="email"
            className="input"
            placeholder="newuser@yourclinic.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Full name</label>
          <input
            className="input"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Role</label>
          <select
            className="input"
            value={role}
            onChange={(e) => setRole(e.target.value as 'ADMIN' | 'PROVIDER')}
          >
            <option value="PROVIDER">Provider</option>
            <option value="ADMIN">Admin</option>
          </select>
        </div>
        <div>
          <label className="label">Temporary password (≥8 chars)</label>
          <input
            type="password"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="mt-1 text-2xs text-ink-subtle">
            Share with the new user out-of-band. They should change it on first login.
          </p>
        </div>
      </div>
      <footer className="mt-4 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn-primary"
          disabled={!email || !fullName || password.length < 8 || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'Creating…' : 'Create user'}
        </button>
      </footer>
    </Modal>
  );
}

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: User;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [fullName, setFullName] = useState(user.fullName);
  const [role, setRole] = useState<'ADMIN' | 'PROVIDER'>(
    user.role === 'STAFF' ? 'PROVIDER' : (user.role as 'ADMIN' | 'PROVIDER'),
  );
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/users/${user.id}`, { fullName, role }).then((r) => r.data),
    onSuccess: () => {
      toast.success('User updated');
      onSaved();
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Update failed'),
  });
  return (
    <Modal open onClose={onClose} title={`Edit ${user.email}`} size="md">
      <div className="space-y-3">
        <div>
          <label className="label">Email</label>
          <input className="input" disabled value={user.email} />
          <p className="mt-1 text-2xs text-ink-subtle">Email is immutable — create a new user if needed.</p>
        </div>
        <div>
          <label className="label">Full name</label>
          <input
            className="input"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Role</label>
          <select
            className="input"
            value={role}
            onChange={(e) => setRole(e.target.value as 'ADMIN' | 'PROVIDER')}
          >
            <option value="PROVIDER">Provider</option>
            <option value="ADMIN">Admin</option>
          </select>
          {user.role === 'STAFF' && (
            <p className="mt-1 text-2xs text-amber-700">
              This user has the legacy STAFF role. Saving will promote them to PROVIDER (functionally equivalent).
            </p>
          )}
        </div>
      </div>
      <footer className="mt-4 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </footer>
    </Modal>
  );
}

function ResetPasswordModal({
  user,
  onClose,
  onReset,
}: {
  user: User;
  onClose: () => void;
  onReset: () => void;
}) {
  const [password, setPassword] = useState('');
  const reset = useMutation({
    mutationFn: () =>
      api.post(`/users/${user.id}/reset-password`, { password }).then((r) => r.data),
    onSuccess: () => {
      toast.success('Password reset. Tell the user out-of-band.');
      onReset();
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Reset failed'),
  });
  return (
    <Modal open onClose={onClose} title={`Reset password — ${user.email}`} size="md">
      <p className="mb-3 text-sm text-ink-subtle">
        Sets a new password for this user. You'll need to share the value with them through a
        secure side-channel (in-person, signal, etc.). They should change it on first login.
      </p>
      <div>
        <label className="label">New password (≥8 chars)</label>
        <input
          type="password"
          className="input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <footer className="mt-4 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn-primary"
          disabled={password.length < 8 || reset.isPending}
          onClick={() => reset.mutate()}
        >
          {reset.isPending ? 'Resetting…' : 'Reset password'}
        </button>
      </footer>
    </Modal>
  );
}
